import { configClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import {
  toAttachment,
  toChange,
  toNodeDetail,
  toRelease,
  toSection,
  toValue,
  toWriteResult,
} from '../mappers/taxonomy-studio.mappers';
import type {
  AttachmentApi,
  ChangeApi,
  NodeDetailApi,
  ReleaseApi,
  SectionApi,
  ValueApi,
  WriteEnvelopeApi,
} from '../types/taxonomy-studio.api.types';
import type {
  Attachment,
  Change,
  Confidence,
  GmcMapping,
  NodeDetail,
  Release,
  Section,
  TaxonomyValue,
  ValueType,
  WriteResult,
} from '../types/taxonomy-studio.types';

const api = endpoints.taxonomyStudio;

type Method = 'post' | 'patch' | 'put' | 'delete';

interface WriteOptions {
  json?: Record<string, unknown>;
  /** `revision_id` the edit was made against → `If-Match` (412 STALE_ITEM on mismatch). */
  ifMatch?: string | undefined;
}

/** Drops undefined members so PATCH bodies only carry fields the user touched. */
function compact(body: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(body).filter(([, v]) => v !== undefined));
}

/** Sends one write and unwraps the envelope, mapping `result` with `map`. */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- TWire names the envelope's wire shape for `map`
async function write<TWire, T>(
  method: Method,
  path: string,
  map: (r: TWire) => T,
  opts: WriteOptions = {},
): Promise<WriteResult<T>> {
  const headers: Record<string, string> = {};
  if (opts.ifMatch) headers['If-Match'] = `"${opts.ifMatch}"`;
  const wire = await configClient[method](path, {
    ...(opts.json ? { json: compact(opts.json) } : {}),
    headers,
  }).json<WriteEnvelopeApi<TWire>>();
  return toWriteResult(wire, map);
}

const itemsCount = (r: { items: number }) => r.items;
const identity = <T>(r: T) => r;

// ── Draft ────────────────────────────────────────────────────────────────────

/** Publishes the draft; 409 DRAFT_CHANGED / CHECKS_FAILED / NO_CHANGES surface to the caller. */
export async function publishDraft(body: {
  name?: string | undefined;
  notes?: string | undefined;
  expectedDraftVersion: number;
}): Promise<Release> {
  const wire = await configClient
    .post(api.draftPublish(), {
      json: compact({
        name: body.name,
        notes: body.notes,
        expected_draft_version: body.expectedDraftVersion,
      }),
    })
    .json<{ release: ReleaseApi }>();
  return toRelease(wire.release);
}

/** Discards the open draft (the release becomes DISCARDED). */
export async function discardDraft(): Promise<Release> {
  const wire = await configClient.delete(api.draft()).json<{ release: ReleaseApi }>();
  return toRelease(wire.release);
}

/** Renames / annotates the open draft. */
export async function patchDraft(body: {
  name?: string | undefined;
  notes?: string | undefined;
}): Promise<Release> {
  const wire = await configClient.patch(api.draft(), { json: compact(body) }).json<ReleaseApi>();
  return toRelease(wire);
}

export function undoAction(actionId: string) {
  return write<{ items: number }, number>('post', api.draftActionUndo(actionId), itemsCount);
}

export function revertChange(changeId: string) {
  return write<ChangeApi, Change>('post', api.draftChangeRevert(changeId), toChange);
}

export function revertChanges(changeIds: string[]) {
  return write<{ items: number }, number>('post', api.draftChangesRevert(), itemsCount, {
    json: { change_ids: changeIds },
  });
}

export function restoreRelease(
  seq: number,
  body: { name?: string | undefined; reason?: string | undefined },
) {
  return write<ReleaseApi, Release>('post', api.releaseRestore(seq), toRelease, { json: body });
}

// ── Nodes ────────────────────────────────────────────────────────────────────

export function createNode(body: {
  name: string;
  parentId: number | null;
  reason?: string | undefined;
}) {
  return write<NodeDetailApi, NodeDetail>('post', api.nodes(), toNodeDetail, {
    json: { name: body.name, parent_id: body.parentId, reason: body.reason },
  });
}

export function patchNode(
  id: number,
  body: { name?: string | undefined; parentId?: number | undefined; reason?: string | undefined },
  revisionId?: string,
) {
  return write<NodeDetailApi, NodeDetail>('patch', api.node(id), toNodeDetail, {
    json: { name: body.name, parent_id: body.parentId, reason: body.reason },
    ifMatch: revisionId,
  });
}

export function archiveNode(id: number, reason?: string) {
  return write<NodeDetailApi, NodeDetail>('post', api.nodeArchive(id), toNodeDetail, {
    json: { reason },
  });
}

export function restoreNode(id: number, reason?: string) {
  return write<NodeDetailApi, NodeDetail>('post', api.nodeRestore(id), toNodeDetail, {
    json: { reason },
  });
}

export type NodeBatchBody =
  | { op: 'MOVE'; nodeIds: number[]; parentId: number; reason?: string | undefined }
  | { op: 'ARCHIVE' | 'RESTORE'; nodeIds: number[]; reason?: string | undefined }
  | {
      op: 'ATTACH';
      nodeIds: number[];
      sectionKey: string;
      confidence?: Confidence | undefined;
      provisional?: boolean | undefined;
      reason?: string | undefined;
    };

/** One bulk node action (all-or-nothing, one undo). */
export function batchNodes(body: NodeBatchBody) {
  const json: Record<string, unknown> = {
    op: body.op,
    node_ids: body.nodeIds,
    reason: body.reason,
  };
  if (body.op === 'MOVE') json.parent_id = body.parentId;
  if (body.op === 'ATTACH') {
    json.section_key = body.sectionKey;
    json.confidence = body.confidence;
    json.provisional = body.provisional;
  }
  return write<{ items: number }, number>('post', api.nodesBatch(), itemsCount, { json });
}

// ── Sections ─────────────────────────────────────────────────────────────────

export interface SectionInput {
  key?: string | undefined;
  name: string;
  valueType: ValueType;
  units?: string[] | undefined;
  definition?: string | undefined;
  labelEnGb?: string | undefined;
  gmc?: GmcMapping[] | undefined;
  reason?: string | undefined;
}

const gmcToWire = (m: GmcMapping) => ({
  field: m.field,
  relation: m.relation,
  declared_by: m.declaredBy,
  note: m.note,
});

export function createSection(body: SectionInput) {
  return write<SectionApi, Section>('post', api.sections(), toSection, {
    json: {
      key: body.key === '' ? undefined : body.key,
      name: body.name,
      value_type: body.valueType,
      units: body.units,
      definition: body.definition,
      label_en_gb: body.labelEnGb,
      gmc: body.gmc?.map(gmcToWire),
      reason: body.reason,
    },
  });
}

export function patchSection(
  key: string,
  body: Partial<Omit<SectionInput, 'key' | 'gmc'>>,
  revisionId?: string,
) {
  return write<SectionApi, Section>('patch', api.section(key), toSection, {
    json: {
      name: body.name,
      value_type: body.valueType,
      units: body.units,
      definition: body.definition,
      label_en_gb: body.labelEnGb,
      reason: body.reason,
    },
    ifMatch: revisionId,
  });
}

export function archiveSection(key: string, reason?: string) {
  return write<SectionApi, Section>('post', api.sectionArchive(key), toSection, {
    json: { reason },
  });
}

export function restoreSection(key: string, reason?: string) {
  return write<SectionApi, Section>('post', api.sectionRestore(key), toSection, {
    json: { reason },
  });
}

export function putGmcMappings(key: string, mappings: GmcMapping[], reason?: string) {
  return write<unknown, unknown>('put', api.sectionGmcMappings(key), identity, {
    json: { mappings: mappings.map(gmcToWire), reason },
  });
}

export type SectionBatchBody =
  | { op: 'ARCHIVE' | 'RESTORE'; keys: string[]; reason?: string | undefined }
  | {
      op: 'ATTACH';
      keys: string[];
      nodeIds: number[];
      confidence?: Confidence | undefined;
      provisional?: boolean | undefined;
      reason?: string | undefined;
    };

export function batchSections(body: SectionBatchBody) {
  const json: Record<string, unknown> = { op: body.op, keys: body.keys, reason: body.reason };
  if (body.op === 'ATTACH') {
    json.node_ids = body.nodeIds;
    json.confidence = body.confidence;
    json.provisional = body.provisional;
  }
  return write<{ items: number }, number>('post', api.sectionsBatch(), itemsCount, { json });
}

// ── Values ───────────────────────────────────────────────────────────────────

export interface ValueInput {
  name?: string | undefined;
  key?: string | undefined;
  synonyms?: string[] | undefined;
  labelEnGb?: string | undefined;
  reason?: string | undefined;
}

export function createValue(sectionKey: string, body: ValueInput & { name: string }) {
  return write<ValueApi, TaxonomyValue>('post', api.sectionValues(sectionKey), toValue, {
    json: {
      name: body.name,
      key: body.key === '' ? undefined : body.key,
      synonyms: body.synonyms,
      label_en_gb: body.labelEnGb,
      reason: body.reason,
    },
  });
}

export function patchValue(
  sectionKey: string,
  valueKey: string,
  body: ValueInput,
  revisionId?: string,
) {
  return write<ValueApi, TaxonomyValue>('patch', api.sectionValue(sectionKey, valueKey), toValue, {
    json: {
      name: body.name,
      synonyms: body.synonyms,
      label_en_gb: body.labelEnGb,
      reason: body.reason,
    },
    ifMatch: revisionId,
  });
}

export function archiveValue(sectionKey: string, valueKey: string) {
  return write<ValueApi, TaxonomyValue>(
    'post',
    api.sectionValueArchive(sectionKey, valueKey),
    toValue,
  );
}

export function restoreValue(sectionKey: string, valueKey: string) {
  return write<ValueApi, TaxonomyValue>(
    'post',
    api.sectionValueRestore(sectionKey, valueKey),
    toValue,
  );
}

export function batchValues(
  sectionKey: string,
  body: { op: 'ARCHIVE' | 'RESTORE'; valueKeys: string[]; reason?: string | undefined },
) {
  return write<{ items: number }, number>('post', api.sectionValuesBatch(sectionKey), itemsCount, {
    json: { op: body.op, value_keys: body.valueKeys, reason: body.reason },
  });
}

export function mergeValues(
  sectionKey: string,
  body: { keep: string; merge: string[]; reason?: string | undefined },
) {
  return write<unknown, unknown>('post', api.sectionValuesMerge(sectionKey), identity, {
    json: body,
  });
}

export function putValueScope(sectionKey: string, valueKey: string, nodeIds: number[]) {
  return write<unknown, unknown>('put', api.sectionValueScope(sectionKey, valueKey), identity, {
    json: { node_ids: nodeIds },
  });
}

export function deleteValueScope(sectionKey: string, valueKey: string) {
  return write<unknown, unknown>('delete', api.sectionValueScope(sectionKey, valueKey), identity);
}

// ── Attachments ──────────────────────────────────────────────────────────────

export function putAttachment(
  sectionKey: string,
  nodeId: number,
  body: {
    confidence?: Confidence | undefined;
    provisional?: boolean | undefined;
    reason?: string | undefined;
  },
) {
  return write<AttachmentApi, Attachment>(
    'put',
    api.sectionAttachment(sectionKey, nodeId),
    toAttachment,
    { json: body },
  );
}

export function detachAttachment(sectionKey: string, nodeId: number) {
  return write<AttachmentApi, Attachment>(
    'delete',
    api.sectionAttachment(sectionKey, nodeId),
    toAttachment,
  );
}

export function restoreAttachment(sectionKey: string, nodeId: number) {
  return write<AttachmentApi, Attachment>(
    'post',
    api.sectionAttachmentRestore(sectionKey, nodeId),
    toAttachment,
  );
}
